/**
 * Shared helper for tracker filter resolution logic.
 *
 * Centralizes the decision of what `selectedItems` payload to send to
 * `getTrackerData` and `getTrackerById` APIs.
 *
 * State model per tracker (tracked via `_filtersInteracted` flag):
 *   _filtersInteracted === false/undefined  →  initial load, use permanent filters
 *   _filtersInteracted === true, appliedFilters = []    →  user explicitly cleared, send empty
 *   _filtersInteracted === true, appliedFilters = [...]  →  user chose specific filters, send those
 */

export interface TrackerFilter {
    column: string;
    values: string[];
}

export interface TrackerFilterApiItem {
    columnName: string;
    item: string[];
}

export type FilterPayload = { selectedItems: TrackerFilterApiItem[] } | null;

export class TrackerFilterHelper {

    /**
     * Normalize a single filter from either `{ column, values }` or `{ columnName, item }` shape.
     */
    static normalizeFilter(f: any): TrackerFilter | null {
        const column = f?.column ?? f?.columnName;
        const values = f?.values ?? f?.item;
        if (!column) return null;
        return {
            column: String(column),
            values: Array.isArray(values) ? values : [],
        };
    }

    /**
     * Normalize an array of filters to the standard `{ column, values }` shape.
     */
    static normalizeFilters(filters: any[]): TrackerFilter[] {
        if (!Array.isArray(filters)) return [];
        return filters
            .map(f => this.normalizeFilter(f))
            .filter((f): f is TrackerFilter => f !== null);
    }

    /**
     * Convert standard filters to the API `{ columnName, item }` shape.
     */
    static toApiFormat(filters: TrackerFilter[]): TrackerFilterApiItem[] {
        return filters.map(f => ({ columnName: f.column, item: f.values }));
    }

    /**
     * Get permanent filters from a tracker object (from stream / backend).
     */
    static getPermanentFilters(tracker: any): TrackerFilter[] {
        const raw = tracker?.defaultFilters || tracker?.filters || [];
        return this.normalizeFilters(raw).filter(f => f.values.length > 0);
    }

    /**
     * Core resolution: build the `{ selectedItems }` API payload.
     *
     * When `hasUserInteracted` is true the user's choice is respected - even
     * if it's an empty array (meaning "show unfiltered data").
     *
     * When `hasUserInteracted` is false (initial / reload), permanent filters
     * are used as the default. Returns `null` when there is nothing to send.
     */
    static buildPayload(opts: {
        hasUserInteracted: boolean;
        appliedFilters?: any[];
        permanentFilters?: any[];
    }): FilterPayload {
        const { hasUserInteracted, appliedFilters, permanentFilters } = opts;

        if (hasUserInteracted) {
            const normalized = this.normalizeFilters(appliedFilters || []);
            const withValues = normalized.filter(f => f.values.length > 0);
            return { selectedItems: this.toApiFormat(withValues) };
        }

        const pFilters = this.normalizeFilters(permanentFilters || []).filter(
            f => f.values.length > 0,
        );
        return pFilters.length > 0
            ? { selectedItems: this.toApiFormat(pFilters) }
            : null;
    }

    /**
     * Build payload directly from a tracker object
     * (reads `_filtersInteracted`, `appliedFilters`, `defaultFilters` / `filters`).
     */
    static buildPayloadFromTracker(tracker: any): FilterPayload {
        return this.buildPayload({
            hasUserInteracted: tracker?._filtersInteracted === true,
            appliedFilters: tracker?.appliedFilters,
            permanentFilters: tracker?.defaultFilters || tracker?.filters,
        });
    }

    /**
     * Set applied filters on a tracker and mark as user-interacted.
     * `selectedItems` may be in either `{ column, values }` or `{ columnName, item }` shape.
     */
    static setAppliedFilters(tracker: any, selectedItems: any[]): void {
        if (!tracker) return;
        tracker.appliedFilters = this.normalizeFilters(selectedItems).map(
            f => ({ column: f.column, values: f.values }),
        );
        tracker._filtersInteracted = true;
    }

    /**
     * Clear all applied filters on a tracker (explicit user action).
     */
    static clearAppliedFilters(tracker: any): void {
        if (!tracker) return;
        tracker.appliedFilters = [];
        tracker._filtersInteracted = true;
    }

    /**
     * Restore filter interaction state from a dialog result back onto a tracker.
     */
    static restoreState(
        tracker: any,
        result: { appliedFilters?: any[]; filtersInteracted?: boolean },
    ): void {
        if (!tracker) return;
        if (result.filtersInteracted) {
            tracker._filtersInteracted = true;
            tracker.appliedFilters = this.normalizeFilters(
                result.appliedFilters || [],
            ).map(f => ({ column: f.column, values: f.values }));
        } else if (
            Array.isArray(result.appliedFilters) &&
            result.appliedFilters.length > 0
        ) {
            tracker.appliedFilters = this.normalizeFilters(
                result.appliedFilters,
            ).map(f => ({ column: f.column, values: f.values }));
        }
    }

    /**
     * Decide which filters to show in the filter dialog as pre-selected values.
     * If user has interacted → show their last choice (even if empty).
     * Otherwise → show applied filters if present, else fall back to permanent/default filters.
     */
    static getFiltersForDialog(
        tracker: any,
        defaultFilters: any[],
    ): any[] {
        if (tracker?._filtersInteracted) {
            return tracker?.appliedFilters || [];
        }
        return tracker?.appliedFilters?.length > 0
            ? tracker.appliedFilters
            : defaultFilters;
    }
}
